import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';

import { ClientOnly } from '@atc/react-client-only';
import { useDevice } from '@atc/react-device';

import { EmailCTA } from 'reaxl-email';
import { useFeatures } from 'reaxl-features';
import {
    OwnerAddress,
    OwnerChat,
    OwnerPhoneNumber,
    OwnerTextCTA,
} from 'reaxl-molecules';

import { vdpResultsDuck } from '@/ducks/vdp';

import useDealerTextMessage from '@/hooks/useDealerTextMessage';
/**
 *  OwnerContact displays Dealer, Text, Chat, Phone, Email, Directions CTAs with icons.
 *  TODO: update OwnerTextCTA, OwnerPhoneNumber, EmailCTA, OwnerAddress to use IconButton
 */

const OwnerContact = memo(({
    analyticsData,
    onEmailCTAClick,
    chatClickHandler,
    textClickHandler,
    phoneClickHandler,
    contactButtonUiContext = 'iconbutton',
}) => {

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const textBody = useDealerTextMessage();

    const {
        chat: [
            isChatEnabled,
            {
                providers: chatProviders,
                enable_mobile_chat: isMobileChatEnabled,
            },
        ],
    } = useFeatures(['chat']);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const { id: inventoryId, owner = {}, marketExtension = {}, clickType } = listing;
    const { chat = {}, location, phone, textNumber, hideEmailForm } = owner;
    const showChatCTA = isChatEnabled && (!isXs || isMobileChatEnabled) && !!chat?.provider && chatProviders?.includes(chat?.provider);
    const { isVirtualDealer } = marketExtension;

    const ctas = [];
    if (phone?.value && isXs) {
        ctas.push(['phone',
            <OwnerPhoneNumber
                analyticsData={analyticsData}
                phoneClickHandler={phoneClickHandler}
                phone={phone}
                uiContext={contactButtonUiContext}
                buttonStyle="link"
                buttonLabel="Phone"
                key="phone-cta-owner-phone"
            />,
        ]);
    }
    if (textNumber && isXs) {
        ctas.push(['text',
            <OwnerTextCTA
                analyticsData={{
                    ...analyticsData,
                    placement: 'dealer-card',
                }}
                textClickHandler={textClickHandler}
                key="text-cta-owner-text"
                label="Text"
                phoneNumber={textNumber}
                textBody={textBody}
                uiContext={contactButtonUiContext}
            />,
        ]);
    }
    if (!hideEmailForm) {
        ctas.push(['email',
            <EmailCTA
                type="iconbutton"
                key="email-cta-connected-email"
                listingId={inventoryId}
                onClick={onEmailCTAClick}
                clickType={clickType}
                context="ownerDetails"
                labelKey="email"
            />,
        ]);
    }
    if (showChatCTA) {
        ctas.push(['chat', (
            <ClientOnly key="owner-detail-chat-cta">
                <OwnerChat
                    {...chat}
                    analyticsData={analyticsData}
                    chatClickHandler={chatClickHandler}
                    listing={listing}
                    merchantId={owner.id}
                    uiContext="iconButtonStacked"
                />
            </ClientOnly>
        )]);
    }
    if (location?.map && !isVirtualDealer) {
        ctas.push(['directions',
            <OwnerAddress
                analyticsData={analyticsData}
                key="address-cta-owner-address"
                location={location}
                uiContext="iconbutton"
            />,
        ]);
    }

    if (!ctas.length) return null;

    const ownerContactCTAsClasses = clsx('display-flex align-items-end', {
        'justify-content-between': ctas.length > 3,
        'justify-content-left gap-5 gap-sm-7': ctas.length <= 3,
    });

    return (
        <div className={ownerContactCTAsClasses}>
            {ctas.map(([key, cta]) => (
                <div
                    className="display-flex flex-column align-items-center"
                    key={`icon-button-${key}`}
                >
                    {cta}
                </div>
            ))}
        </div>

    );
});

OwnerContact.propTypes = {
    analyticsData: PropTypes.object,
    /**
     * The callback function fires when email CTA is clicked
     */
    onEmailCTAClick: PropTypes.func,
    /**
     * The callback function fires when chat CTA is clicked
     */
    chatClickHandler: PropTypes.func,
    /**
     * The callback function fires when owner phone is called
     */
    phoneClickHandler: PropTypes.func,
    /**
     * The callback function fires when text CTA is clicked
     */
    textClickHandler: PropTypes.func,
    /**
     * The uiContext for both phone and text CTAs
     */
    contactButtonUiContext: PropTypes.oneOf([
        'button',
        'iconbutton',
        'iconbutton-horizontal',
    ]),
};

export default OwnerContact;
